import { forwardRef } from 'react'

import { cn } from '@/lib/utils'

/**
 * A Material Symbol, by ligature name — the base every other Fx component
 * uses for its glyphs (`<FxIcon name="check_circle" />`).
 *
 * There is one font family, not two: the filled cut comes from the FILL axis,
 * which `.material-symbols-outlined` in `styles/index.css` reads off
 * `--fx-icon-fill`, so `filled` sets that custom property rather than swapping
 * the family. `size` and `color` are inline because they are per-call values
 * the token layer does not cover; prefer a `text-*` class over `color` where
 * the colour is a token (`className="text-text-muted"`).
 *
 * Hidden from screen readers by default: an icon in the design always sits
 * beside its own word, or inside a control that carries an `aria-label`. Pass
 * `aria-hidden={false}` with a `title`/`aria-label` for the rare icon that is
 * the only carrier of meaning.
 */
export interface FxIconProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'color' | 'children'> {
  /** Material Symbols Outlined ligature name, e.g. `stylus_note`. */
  name: string
  /** Rendered size in px; the box is 1em square, so this drives both axes. */
  size?: number
  /** The filled cut, used for navigation and object badges. */
  filled?: boolean
  /** A CSS colour for the rare case a token class cannot express it. */
  color?: string
}

export const FxIcon = forwardRef<HTMLSpanElement, FxIconProps>(
  ({ name, size = 24, filled = false, color, className, style, ...props }, ref) => (
    <span
      ref={ref}
      aria-hidden
      className={cn('material-symbols-outlined', className)}
      style={
        {
          fontSize: `${size}px`,
          color,
          ...(filled ? { '--fx-icon-fill': 1 } : null),
          ...style,
        } as React.CSSProperties
      }
      {...props}
    >
      {name}
    </span>
  ),
)
FxIcon.displayName = 'FxIcon'
